import { useQuery } from "@apollo/client";
import { Icon, SearchIcon, AddIcon } from "@chakra-ui/icons";
import {
  useMediaQuery,
  useDisclosure,
  SimpleGrid,
  Flex,
  InputGroup,
  InputLeftAddon,
  Input,
  Popover,
  PopoverTrigger,
  Button,
  Portal,
  PopoverContent,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  ButtonGroup,
  IconButton,
  Stack,
  Center,
  Spinner,
  Text,
  Box,
} from "@chakra-ui/react";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import { debounce, get } from "lodash";
import moment from "moment";
import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { FaSync } from "react-icons/fa";
import { MdFilterList } from "react-icons/md";
import ReactSelect from "react-select";
import { GET_COMMODITIES_PUBLIC } from "../../pages/public/queries";
import { PRODUCT_CATEGORIES } from "../../utils/constants";
import Pagination from "../common/Pagination";
import SelectEstablishment from "../common/SelectEstablishment";
import CreateCommodity from "../modals/commodity/create/CreateCommodity";
import CreateMassCommodity from "../modals/commodity/create/CreateMassCommodity";
import CommoditiesTableContainer from "./CommoditiesTableContainer";

type PublicCommoditiesTableProps = {
  isPublic?: boolean;
  similar_categories?: string[];
  showFilters?: boolean;
};

const PublicCommoditiesTable = (props: PublicCommoditiesTableProps) => {
  const {
    isPublic = true,
    similar_categories = [],
    showFilters = true,
  } = props;

  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState(similar_categories);
  const [status, setStatus] = useState(["active"]);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [establishmentId, setEstablishmentId] = useState([]);

  const [page, setPage] = useState(1);
  const [selectedDates, setSelectedDates] = useState<Date[] | any>([
    new Date(moment(new Date()).subtract(1, "month").format()),
    new Date(moment(new Date()).add(1, "month").format()),
  ]);

  const [range, setRange] = useState<{
    min: number | any;
    max?: number | any;
  }>({
    min: 1,
  });

  const { data, loading, refetch } = useQuery(GET_COMMODITIES_PUBLIC, {
    variables: {
      args: {
        ...range,
        categories,
        query: search,
        status,
        establishmentId: establishmentId,
        startDate: selectedDates[0],
        endDate: selectedDates[1],
        pagination: {
          page,
          size: 20,
        },
      },
    },
    fetchPolicy: "network-only",
  });

  const debounceFn = useCallback(
    debounce((val) => {
      setSearch(val);
      setPage(1);
    }, 500),
    []
  );

  const debounceRange = useCallback(
    debounce((val) => {
      setRange(val);
      setPage(1);
    }, 500),
    []
  );

  const debounceCategories = useCallback(
    debounce((val) => {
      setCategories(val);
      setPage(1);
    }, 500),
    []
  );
  const debounceStatus = useCallback(
    debounce((val) => {
      setStatus(val);
      setPage(1);
    }, 500),
    []
  );

  const onSearch = (val: any) => {
    debounceFn(val);
  };

  const onChangeRange = (val: any) => {
    debounceRange(val);
  };

  const onChangeCategories = (val: any) => {
    debounceCategories(val);
  };

  const onChangeStatus = (val: any) => {
    debounceStatus(val);
  };

  const {
    isOpen: isCreateModalOpen,
    onOpen: onOpenCreateModal,
    onClose: onCloseCreateModal,
  } = useDisclosure();

  const {
    isOpen: isMassCreateModalOpen,
    onOpen: onOpenMassCreateModal,
    onClose: onCloseMassCreateModal,
  } = useDisclosure();

  const refetchCommodities = () => {
    setRefetchLoading(true);
    refetch()
      .then(() => setRefetchLoading(false))
      .finally(() => setRefetchLoading(false));
  };

  const commodities = get(data, "publicCommodities.items", []);
  const meta = get(data, "publicCommodities.meta", []);

  return (
    <>
      <Helmet>
        <title>Freeshu | Dashboard</title>
      </Helmet>

      {showFilters && (
        <Box p="5">
          {isLargerThan900 ? (
            <SimpleGrid columns={[1, 2]}>
              <Pagination
                meta={meta}
                page={page}
                setPage={setPage}
                isDisabled={loading}
              />

              <Flex alignItems="center" justifyContent="space-between">
                <InputGroup mx="2">
                  <InputLeftAddon
                    rounded="md"
                    children={<Icon size="2" as={SearchIcon} />}
                  />
                  <Input
                    rounded="md"
                    focusBorderColor="green.500"
                    onChange={(e) => {
                      onSearch(e.target.value);
                    }}
                  />
                </InputGroup>

                <Flex>
                  <Popover>
                    <PopoverTrigger>
                      <Button rightIcon={<MdFilterList />}>Filters</Button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent>
                        <PopoverArrow />
                        <PopoverHeader>Filters</PopoverHeader>
                        <PopoverCloseButton />
                        <PopoverBody>
                          <Flex p="2" flexDirection="column">
                            <Flex>
                              <Flex flexDirection="column">
                                <Text ml="2">Min</Text>
                                <NumberInput
                                  size="md"
                                  min={1}
                                  defaultValue={range.min}
                                  onChange={(price: any) => {
                                    onChangeRange({
                                      ...range,
                                      min: Number(price),
                                    });
                                  }}
                                  mx="2"
                                >
                                  <NumberInputField />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                              </Flex>
                              <Flex flexDirection="column">
                                <Text ml="2">Max</Text>
                                <NumberInput
                                  size="md"
                                  min={1}
                                  defaultValue={range?.max || null}
                                  onChange={(price: any) => {
                                    onChangeRange({
                                      ...range,
                                      max: Number(price),
                                    });
                                  }}
                                  mx="2"
                                >
                                  <NumberInputField />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                              </Flex>
                            </Flex>
                          </Flex>

                          <Flex p="4" flexDirection="column">
                            <Text>Categories</Text>
                            <ReactSelect
                              isMulti
                              name="colors"
                              closeMenuOnSelect={false}
                              options={PRODUCT_CATEGORIES}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              onChange={(val: any) => {
                                onChangeCategories(
                                  val.map((v: any) => v.value)
                                );
                              }}
                            />
                          </Flex>
                          <Flex p="4" flexDirection="column">
                            <Text ml="2">Status</Text>

                            <ReactSelect
                              isMulti
                              closeMenuOnSelect={false}
                              onChange={(val: any) => {
                                onChangeStatus(val.map((v: any) => v.value));
                              }}
                              options={[
                                {
                                  label: "Active",
                                  value: "active",
                                },
                                {
                                  label: "Archived",
                                  value: "archived",
                                },
                              ]}
                            />
                          </Flex>
                          <Flex p="4" flexDirection="column">
                            <Text ml="2">Establishment</Text>
                            <SelectEstablishment
                              onChange={setEstablishmentId}
                            />
                          </Flex>
                          <Flex p="4" flexDirection="column">
                            <Text>Date Range</Text>

                            <RangeDatepicker
                              selectedDates={selectedDates}
                              onDateChange={setSelectedDates}
                              propsConfigs={{
                                dayOfMonthBtnProps: {
                                  defaultBtnProps: {
                                    _hover: {
                                      background: "primary",
                                      color: "white",
                                    },
                                  },
                                  selectedBtnProps: {
                                    background: "primary",
                                    color: "white",
                                  },
                                  // todayBtnProps: {
                                  //   background: "gray.100",
                                  // },
                                },
                                inputProps: {
                                  size: "md",
                                },
                              }}
                            />
                          </Flex>
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </Popover>
                  <ButtonGroup isAttached mr="2">
                    {!isPublic && (
                      <>
                        <Button onClick={onOpenCreateModal}>New</Button>
                        <IconButton
                          aria-label="More"
                          bg="gray.200"
                          icon={<AddIcon />}
                          onClick={onOpenMassCreateModal}
                        />
                      </>
                    )}
                    <ButtonGroup>
                      {refetchLoading ? (
                        <Button mr="2">
                          <Spinner size="sm" />
                        </Button>
                      ) : (
                        <IconButton
                          aria-label="More"
                          bg="gray.200"
                          icon={<FaSync />}
                          isDisabled={loading}
                          onClick={refetchCommodities}
                          ml="2"
                        />
                      )}
                    </ButtonGroup>
                  </ButtonGroup>
                </Flex>
              </Flex>
            </SimpleGrid>
          ) : (
            <Stack>
              <InputGroup p="3">
                <InputLeftAddon
                  rounded="md"
                  children={<Icon size="2" as={SearchIcon} />}
                />
                <Input
                  rounded="md"
                  focusBorderColor="green.500"
                  onChange={(e) => {
                    onSearch(e.target.value);
                  }}
                />
              </InputGroup>

              <Flex alignItems="center" justifyContent="space-between">
                <Pagination
                  meta={meta}
                  page={page}
                  setPage={setPage}
                  isDisabled={loading}
                  showBottomTotal={true}
                  showTotal={false}
                  size="md"
                />
                <Box p="3">
                  <Popover>
                    <PopoverTrigger>
                      <IconButton
                        aria-label="Filters"
                        icon={<MdFilterList />}
                        mx="3"
                      />
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent>
                        <PopoverArrow />
                        <PopoverHeader>Filters</PopoverHeader>
                        <PopoverCloseButton />
                        <PopoverBody>
                          <Flex p="2" flexDirection="column">
                            <Flex>
                              <Flex flexDirection="column">
                                <Text ml="2">Min</Text>
                                <NumberInput
                                  size="md"
                                  min={1}
                                  defaultValue={range.min}
                                  onChange={(price: any) => {
                                    onChangeRange({
                                      ...range,
                                      min: Number(price),
                                    });
                                  }}
                                  mx="2"
                                >
                                  <NumberInputField />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                              </Flex>
                              <Flex flexDirection="column">
                                <Text ml="2">Max</Text>
                                <NumberInput
                                  size="md"
                                  min={1}
                                  defaultValue={range?.max || null}
                                  onChange={(price: any) => {
                                    onChangeRange({
                                      ...range,
                                      max: Number(price),
                                    });
                                  }}
                                  mx="2"
                                >
                                  <NumberInputField />
                                  <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                  </NumberInputStepper>
                                </NumberInput>
                              </Flex>
                            </Flex>

                            <Flex p="4" flexDirection="column">
                              <Text>Categories</Text>
                              <ReactSelect
                                isMulti
                                name="colors"
                                closeMenuOnSelect={false}
                                options={PRODUCT_CATEGORIES}
                                className="basic-multi-select"
                                classNamePrefix="select"
                                onChange={(val: any) => {
                                  onChangeCategories(
                                    val.map((v: any) => v.value)
                                  );
                                }}
                              />
                            </Flex>

                            <Flex p="4" flexDirection="column">
                              <Text ml="2">Status</Text>

                              <ReactSelect
                                isMulti
                                closeMenuOnSelect={false}
                                onChange={(val: any) => {
                                  onChangeStatus(val.map((v: any) => v.value));
                                }}
                                options={[
                                  {
                                    label: "Active",
                                    value: "active",
                                  },
                                  {
                                    label: "Archived",
                                    value: "archived",
                                  },
                                ]}
                              />
                            </Flex>
                            <Flex p="4" flexDirection="column">
                              <Text ml="2">Establishment</Text>
                              <SelectEstablishment
                                onChange={setEstablishmentId}
                              />
                            </Flex>
                            <Flex p="4" flexDirection="column">
                              <Text>Date Range</Text>

                              <RangeDatepicker
                                selectedDates={selectedDates}
                                onDateChange={setSelectedDates}
                                propsConfigs={{
                                  dayOfMonthBtnProps: {
                                    defaultBtnProps: {
                                      _hover: {
                                        background: "primary",
                                        color: "white",
                                      },
                                    },
                                    selectedBtnProps: {
                                      background: "primary",
                                      color: "white",
                                    },
                                  },
                                  inputProps: {
                                    size: "md",
                                  },
                                }}
                              />
                            </Flex>
                          </Flex>
                        </PopoverBody>

                        {/* <PopoverFooter>This is the footer</PopoverFooter> */}
                      </PopoverContent>
                    </Portal>
                  </Popover>

                  {refetchLoading ? (
                    <Spinner />
                  ) : (
                    <IconButton
                      aria-label="Refresh"
                      icon={<FaSync />}
                      onClick={refetchCommodities}
                    />
                  )}
                </Box>
              </Flex>
            </Stack>
          )}
        </Box>
      )}

      <Box mt="6">
        {loading ? (
          <Center py="5">
            <Spinner />
          </Center>
        ) : isLargerThan900 ? (
          <Box p="3" height="70vh" overflow="auto-scroll">
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <CommoditiesTableContainer
              commodities={commodities}
              isPublic={isPublic}
            />
          </Box>
        ) : (
          <>
            {refetchLoading && (
              <Center py="5">
                <Spinner />
              </Center>
            )}
            <CommoditiesTableContainer
              commodities={commodities}
              isPublic={isPublic}
            />
          </>
        )}
      </Box>

      <CreateCommodity
        isOpen={isCreateModalOpen}
        onOpen={onOpenCreateModal}
        onClose={onCloseCreateModal}
        refetch={refetchCommodities}
      />
      <CreateMassCommodity
        isOpen={isMassCreateModalOpen}
        onOpen={onOpenMassCreateModal}
        onClose={onCloseMassCreateModal}
        refetch={refetchCommodities}
      />
      {/* <FiltersDrawer
        isOpen={isFilterOen}
        onOpen={openFilterModal}
        onClose={closeFilterModal}
        onChangeRange={onChangeRange}
        onChangeCategories={onChangeCategories}
        onChangeStatus={onChangeStatus}
        selectedDates={selectedDates}
        setSelectedDates={setSelectedDates}
        range={range}
      /> */}
    </>
  );
};

export default PublicCommoditiesTable;
